const isChromeExtension = false; //chrome && chrome.runtime;
// 导入兼容的WeakRef和FinalizationRegistry
import { getWeakRef, getFinalizationRegistry } from "../utils/compatibility.js";
import { getBrowser, checkClientInstall } from "../utils/index.js";
import { debounce } from "es-toolkit";
const baseUrl = isChromeExtension
  ? chrome.runtime.getURL("./middleware/")
  : `${window.__iWebOffice2015__config.getBaseUrl()}/kg-chrome-plugin/middleware/`;

// 使用兼容的WeakRef实现
const WeakRefPolyfill = getWeakRef();
// 使用兼容的ResizeObserver实现
// 使用兼容的FinalizationRegistry实现
const FinalizationRegistry = getFinalizationRegistry();

/**
 * 要注入的 js 脚本
 */
const scriptsToInject = ["createMiddleware.js", "loadMiddleware.js"];
/**
 * iWebOffice 查询条件
 */
const OBJECT_SELECTORS = [
  'object[clsid="CLSID:D89F482C-5045-4DB5-8C53-D2C9EE71D025"]',
  'object[progid="Kinggrid.iWebOffice"]',
];

/**
 * 注入脚本
 * @returns {Promise<void>} 当所有脚本加载完成后解析
 */
function injectScripts() {
  return Promise.all(
    scriptsToInject.map((src) => {
      return new Promise((resolve) => {
        if ([...document.scripts].some((s) => s.src.includes(src))) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = `${baseUrl}${src}`;
        script.async = false;

        // 脚本加载成功时调用 resolve
        script.onload = () => {
          setTimeout(() => {
            resolve();
          }, 0);
        };

        // 脚本加载失败时调用 reject
        script.onerror = () => {
          console.log(new Error(`Failed to load script: ${src}`));
        };

        document.head.appendChild(script);
      });
    }),
  );
}

/**
 * 注入worker
 */
function injectWorker() {
  return new Promise((resolve) => {
    if (isChromeExtension) {
      fetch(chrome.runtime.getURL("worker/socket.js"))
        .then((response) => response.text())
        .then((scriptContent) => {
          const blob = new Blob([scriptContent], { type: "application/javascript" });
          const value = URL.createObjectURL(blob);

          window.postMessage({ action: "createWorker", value });
          resolve();
        });
    } else {
      const value = `${window.__iWebOffice2015__config.getBaseUrl()}/kg-chrome-plugin/worker/socket.js`;

      window.postMessage({ action: "createWorker", value });
      resolve();
    }
  });
}

// 注入脚本
function injects() {
  // 如果已经有注入操作在进行中，返回同一个Promise
  if (injects._isInjecting) {
    return injects._injectsPromise;
  }

  // 设置状态锁和当前Promise
  injects._isInjecting = true;

  injects._injectsPromise = new Promise((resolve) => {
    injectScripts()
      .then(() => injectWorker())
      .finally(() => {
        injects._isInjecting = false; // 操作完成，释放锁
        resolve();
      });
  });

  return injects._injectsPromise;
}

/**
 * 获取目标元素
 */
function getTargetElement() {
  const firstTarget = OBJECT_SELECTORS.find((selector) => document.querySelector(selector));
  if (!firstTarget) return false;

  // 注入脚本 - 忽略Promise，因为这里只需要触发注入操作
  // 实际的注入结果将在Promise完成时处理
  injects();

  // 检查元素是否有 isFakeObject 属性
  const firstElement = document.querySelector(firstTarget);

  // 移除 PPAPI控件 type属性
  if (firstElement.hasAttribute("type")) {
    firstElement.removeAttribute("type");
  }

  if (!firstElement.hasAttribute("isFakeObject")) return false;

  // 查找所有符合选择器的元素
  const allElements = [];
  for (const selector of OBJECT_SELECTORS) {
    const elements = document.querySelectorAll(selector);
    allElements.push(...elements);
  }

  // 提取获取元素属性的逻辑到单独函数
  const getElementAttributes = (element) => {
    const attributes = {};
    for (const { name, value } of element.attributes) {
      attributes[name] = value;
    }
    return attributes;
  };

  // 映射元素为包含属性和元素本身的对象
  return allElements.map((element) => ({
    attributes: getElementAttributes(element),
    element,
  }));
}

/**
 * 检查元素是否可见
 */
function isElementVisible(el) {
  const rect = el.getBoundingClientRect();
  const display = window.getComputedStyle(el).display;

  // 修改判断逻辑，当 left 和 top 同时小于 0，或者宽度、高度不大于 0，或者 display 为 none 时，判定元素不可见
  return !((rect.left < 0 && rect.top < 0) || rect.width <= 0 || rect.height <= 0 || display === "none");
}

// 存储已经创建的 removalObserver
const elementRemovalObservers = new Map();

/**
 * 检查是否已经存在目标元素，对多个目标元素分别进行监听
 */
function checkTargetElement(observer) {
  const targets = getTargetElement();
  if (!targets) return;

  // 存在后续在添加object 不能关闭监听
  // observer.disconnect();

  targets.forEach((target) => {
    let lastVisible = null;
    let lastWidth = null;
    let lastHeight = null;
    let lastLeft = null;
    let lastTop = null;
    let rafId = null;

    let elementId = target.element.id;
    // 检查该元素是否已经有对应的 removalObserver
    if (elementRemovalObservers.has(elementId)) {
      return;
    }

    const elementRef = new WeakRefPolyfill(target.element);

    // 创建FinalizationRegistry来监听元素引用的销毁
    const finalizationRegistry = new FinalizationRegistry((heldValue) => {
      console.log(`元素 ${heldValue.elementId} 已被垃圾回收`);
      cleanup();
      injectWorker().then(() => {
        window.postMessage({ action: "destroyed", targetId: heldValue.elementId }, "*");
        // 元素销毁后，从 Map 中移除对应的 observer
        elementRemovalObservers.delete(heldValue.elementId);
      });
    });

    // 注册元素引用
    finalizationRegistry.register(target.element, { elementId }, target.element);

    const removalObserver = new MutationObserver((mutations) => {
      const currentElement = elementRef.deref();
      if (!currentElement) return;
      if (!currentElement.getAttribute("isFakeObject")) return;

      if (!document.contains(currentElement)) {
        cleanup();
        injectWorker().then(() => {
          console.log("object 元素销毁了！");
          window.postMessage({ action: "destroyed", targetId: target.element.id }, "*");
          // observer.disconnect();
          observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
          });

          // 元素销毁后，从 Map 中移除对应的 observer
          elementRemovalObservers.delete(elementId);
        });
      }
    });

    // 将 removalObserver 存储到 Map 中
    elementRemovalObservers.set(elementId, removalObserver);

    const cleanup = () => {
      removalObserver.disconnect();
      attrObserver.disconnect();
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      // 清理FinalizationRegistry
      finalizationRegistry.unregister(target.element);
      if (typeof finalizationRegistry.cleanup === "function") {
        finalizationRegistry.cleanup();
      }
    };

    // 尺寸检查
    const _checkResize = () => {
      const rect = target.element.getBoundingClientRect();
      const curWidth = rect.width;
      const curHeight = rect.height;

      if (curWidth !== lastWidth || curHeight !== lastHeight) {
        lastWidth = curWidth;
        lastHeight = curHeight;

        window.postMessage({ action: "debounceResize", targetId: target.element.id }, "*");
      }
    };

    const _checkVisibility = () => {
      const curVisible = isElementVisible(target.element);

      if (lastVisible !== curVisible) {
        lastVisible = curVisible;
        const value = curVisible ? "show" : "hide";
        window.postMessage({ action: "display", value, targetId: target.element.id }, "*");
      }
    };

    const checkResize = debounce(_checkResize, 300);
    const checkVisibility = debounce(_checkVisibility, 300);

    const createObservers = () => {
      // 尺寸观察器
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(checkResize);
      });

      // 属性观察器
      const attrObserver = new MutationObserver((mutations) => {
        if (mutations.some((m) => m.attributeName === "style" || m.attributeName === "class")) {
          checkVisibility();
        }
      });

      // 交叉观察器
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => checkVisibility());
        },
        { threshold: 0.01 },
      );

      // 使用 requestAnimationFrame 定期检查
      const checkPosition = () => {
        const check = () => {
          const currentElement = elementRef.deref();
          if (!currentElement) {
            // 元素不存在，停止 requestAnimationFrame 循环
            cancelAnimationFrame(rafId);
            return;
          }
          const rect = currentElement.getBoundingClientRect();
          const curLeft = rect.left;
          const curTop = rect.top;

          let shouldContinue = isElementVisible(currentElement);

          if (curLeft !== lastLeft || curTop !== lastTop) {
            lastLeft = curLeft;
            lastTop = curTop;

            window.postMessage({ action: "debounceResize", targetId: currentElement.id }, "*");
            shouldContinue = true;
          }

          if (shouldContinue) {
            rafId = requestAnimationFrame(check);
          } else {
            // 元素不可见且状态无变化，停止监听
            cancelAnimationFrame(rafId);
          }
        };
        check();
      };

      return {
        attrObserver,
        intersectionObserver,
        resizeObserver,
        removalObserver,
        checkPosition,
      };
    };

    // 启动观察
    const { attrObserver, intersectionObserver, resizeObserver, checkPosition } = createObservers();
    attrObserver.observe(target.element, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    checkPosition();
    intersectionObserver.observe(target.element);
    resizeObserver.observe(target.element);
    removalObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  });
}

/**
 * 主函数
 */
(function () {
  const { usePPAPI } = checkClientInstall();
  const { browser, version } = getBrowser();
  console.log("🚀 ~ version:", version);

  const isChrome = browser == "chrome";
  const isHighVersionChrome = isChrome && version >= 107;
  const isLowVersionChrome = isChrome && version < 107 && version >= 76;
  const isFirefox = browser == "firefox";
  const is360 = browser == "360";

  const allows = [
      // 1. Chrome浏览器 >= 107
      isHighVersionChrome,
      // 2. 不使用PPAPI 且 Chrome浏览器 < 107
      // 3. 不使用PPAPI 且 360浏览器
      // 4. 不使用PPAPI 且 Firefox浏览器
      !usePPAPI && (isLowVersionChrome || is360 || isFirefox),
    ];
  if (allows.some((item) => item)) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          checkTargetElement(observer);
        }
      });
    });

    // 开始观察整个文档树，包括子节点的变化
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    // 通过配置项控制是否立即注入脚本
    window.__iWebOffice2015__config.immediateInject && injects();

    // 检查是否已经存在目标元素
    checkTargetElement(observer);
  } else {
    console.warn("🚀 ~ 当前浏览器支持PPAPI模式");
  }
})();
